import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Steps,
  Form,
  Input,
  Button,
  Card,
  Switch,
  Progress,
  Spin,
  message,
  Space,
  Divider,
  Typography,
  Row,
  Col,
  Tag,
  Timeline,
  Table,
  Popconfirm,
  Tooltip,
  Tabs
} from 'antd';
import {
  CloudServerOutlined,
  UserOutlined,
  SettingOutlined,
  DatabaseOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  EditOutlined,
  EyeOutlined,
  StopOutlined
} from '@ant-design/icons';
import { tenantInitService } from '../services/tenantInitService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3007/api';

const { Step } = Steps;
const { Title, Text } = Typography;

interface TenantInitRequest {
  tenantCode: string;
  tenantName: string;
  adminUser: {
    username: string;
    password: string;
    email?: string;
  };
  services: {
    sapB1?: {
      serverUrl: string;
      username: string;
      password: string;
      company: string;
    };
    documentParsing?: {
      serverUrl: string;
      username: string;
      password: string;
    };
    chatbi?: {
      serverUrl: string;
      username: string;
      password: string;
    };
  };
  dataInit: {
    includeModeling: boolean;
    includeInstructions: boolean;
    includeTemplates: boolean;
    includePermissions: boolean;
    syncToRemote: boolean;
    syncModeling: boolean;
    syncInstructions: boolean;
    syncTemplates: boolean;
    syncPermissions: boolean;
  };
}

interface InitStep {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  message?: string;
  error?: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
}

interface InitProgress {
  tenantCode: string;
  totalSteps: number;
  completedSteps: number;
  currentStep: string;
  status: 'initializing' | 'completed' | 'failed';
  steps: InitStep[];
  startTime: string;
  endTime?: string;
  totalDuration?: number;
}

interface TenantInitWizardProps {
  visible?: boolean;
  onClose?: () => void;
  onSuccess?: (tenantCode: string) => void;
  onComplete?: () => void;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
  tenantCode?: string;
  initialData?: {
    tenantCode: string;
    tenantName: string;
    adminUser: any;
    services: any;
    dataInit: any;
  };
}

const TenantInitWizard: React.FC<TenantInitWizardProps> = ({
  visible = true,
  onClose,
  onSuccess,
  onComplete,
  onCancel,
  mode = 'create',
  tenantCode: propTenantCode,
  initialData
}) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [initProgress, setInitProgress] = useState<InitProgress | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [progressPolling, setProgressPolling] = useState<NodeJS.Timeout | null>(null);
  const [tenantState, setTenantState] = useState<any>(null);
  const [jobDetails, setJobDetails] = useState<string>('');
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [currentStepLog, setCurrentStepLog] = useState<any>(null);
  const [stepLogs, setStepLogs] = useState<string>('');
  const [realTimeLogs, setRealTimeLogs] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [currentStepId, setCurrentStepId] = useState<string>('');

  // 步骤配置
  const stepConfigs = [
    {
      title: '基本信息',
      icon: <CloudServerOutlined />,
      description: '设置租户基本信息'
    },
    {
      title: '管理员用户',
      icon: <UserOutlined />,
      description: '创建租户管理员'
    },
    {
      title: '服务配置',
      icon: <SettingOutlined />,
      description: '配置外部服务'
    },
    {
      title: '数据初始化',
      icon: <DatabaseOutlined />,
      description: '选择要初始化的数据'
    },
    {
      title: '开始初始化',
      icon: <SyncOutlined />,
      description: '执行初始化流程'
    }
  ];

  // 清理定时器
  useEffect(() => {
    return () => {
      if (progressPolling) {
        clearInterval(progressPolling);
      }
    };
  }, [progressPolling]);

  // 组件挂载时立即加载租户状态
  useEffect(() => {
    const loadInitialState = async () => {
      const tenantCode = propTenantCode || form.getFieldValue('tenantCode');
      if (tenantCode) {
        console.log('🔄 组件挂载，立即加载租户状态，租户代码:', tenantCode);
        await loadTenantState(tenantCode);
      } else if (initialData?.tenantCode) {
        console.log('🔄 组件挂载，使用初始数据加载租户状态，租户代码:', initialData.tenantCode);
        await loadTenantState(initialData.tenantCode);
      } else {
        console.log('🔄 组件挂载，没有租户代码，等待用户输入');
      }
    };
    
    loadInitialState();
  }, [propTenantCode]); // 添加 propTenantCode 作为依赖，确保租户代码变化时重新加载

  // 监听 tenantState 变化
  useEffect(() => {
    if (tenantState) {
      console.log('🔄 tenantState 已更新:', tenantState);
    } else {
      console.log('🔄 tenantState 为空');
    }
  }, [tenantState]);

  // 当进入步骤3（数据初始化选项）时，从状态文件加载dataInit值
  useEffect(() => {
    if (currentStep === 3) {
      const tenantCode = propTenantCode || form.getFieldValue('tenantCode');
      if (tenantCode) {
        console.log('🔄 检测到进入步骤3，开始加载dataInit值，租户代码:', tenantCode);
        loadDataInitFromState(tenantCode);
      }
    }
  }, [currentStep, propTenantCode]);

  // 当进入步骤4（开始初始化）时，重新加载租户状态
  useEffect(() => {
    if (currentStep === 4) {
      const tenantCode = propTenantCode || form.getFieldValue('tenantCode');
      if (tenantCode) {
        console.log('🔄 检测到进入步骤4，重新加载租户状态，租户代码:', tenantCode);
        loadTenantState(tenantCode);
      } else {
        console.log('⚠️ 步骤4：没有找到租户代码，无法加载租户状态');
      }
    }
  }, [currentStep, propTenantCode]);

  // 开始轮询进度
  const startProgressPolling = (tenantCode: string) => {
    const poll = setInterval(async () => {
      try {
        const progress = await tenantInitService.getInitProgress(tenantCode);
        setInitProgress(progress);
        
        if (progress.status === 'completed' || progress.status === 'failed') {
          clearInterval(poll);
          setProgressPolling(null);
          setIsInitializing(false);
          
          if (progress.status === 'completed') {
            message.success('多租户初始化完成！');
            if (onSuccess) {
              onSuccess(tenantCode);
            }
          } else {
            message.error('多租户初始化失败！');
          }
        }
      } catch (error) {
        console.error('获取初始化进度失败:', error);
      }
    }, 2000);
    
    setProgressPolling(poll);
  };

  // 保存配置（编辑模式）
  const handleSaveConfig = async () => {
    try {
      const values = await form.validateFields();
      console.log('📝 保存配置数据:', values);
      
      // 构建完整的步骤数据
      const stepData = {
        tenantCode: values.tenantCode,
        tenantName: values.tenantName,
        adminUser: {
          username: values.adminUsername || '',
          password: values.adminPassword || '',
          email: values.adminEmail || ''
        },
        services: {
          sapB1: values.enableSapB1 ? {
            serverUrl: values.sapServerUrl || '',
            username: values.sapUsername || '',
            password: values.sapPassword || '',
            company: values.sapCompany || ''
          } : undefined,
          documentParsing: values.enableDocumentParsing ? {
            serverUrl: values.docServerUrl || '',
            username: values.docUsername || '',
            password: values.docPassword || ''
          } : undefined,
          chatbi: values.enableChatbi ? {
            serverUrl: values.chatbiServerUrl || '',
            username: values.chatbiUsername || '',
            password: values.chatbiPassword || ''
          } : undefined
        },
        dataInit: {
          includeModeling: values.includeModeling !== undefined ? values.includeModeling : true,
          includeInstructions: values.includeInstructions !== undefined ? values.includeInstructions : true,
          includeTemplates: values.includeTemplates !== undefined ? values.includeTemplates : true,
          includePermissions: values.includePermissions !== undefined ? values.includePermissions : true,
          syncToRemote: values.syncToRemote !== undefined ? values.syncToRemote : false
        }
      };
      
      // 保存配置到状态文件
      await saveStepState('modeling-init-config', stepData, 'completed');
      
      message.success('配置已保存');
      
      // 调用完成回调
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    }
  };

  // 开始初始化
  const handleStartInit = async () => {
    try {
      console.log('🚀 开始初始化 - 调试信息');
      console.log('📋 当前步骤:', currentStep);
      console.log('📋 模式:', mode);
      console.log('📋 租户状态:', tenantState);
      
      // 在步骤4中，优先使用tenantState，不需要表单验证
      let tenantCode, tenantName, values;
      
      if (tenantState) {
        console.log('📋 使用租户状态中的租户信息');
        tenantCode = tenantState.tenantCode;
        tenantName = tenantState.tenantName;
        values = {}; // 不需要表单数据
      } else {
        console.log('📝 从表单获取租户信息');
        // 先获取表单的所有值（不验证）
        const allFormValues = form.getFieldsValue();
        console.log('📝 表单所有字段值（未验证）:', allFormValues);
        
        // 然后验证表单
        values = await form.validateFields();
        console.log('📝 表单验证后的数据:', values);
        
        tenantCode = values.tenantCode || propTenantCode;
        tenantName = values.tenantName || tenantCode;
      }
      
      console.log('🔍 租户信息检查:', { 
        tenantCode,
        tenantName,
        propTenantCode, 
        stateTenantCode: tenantState?.tenantCode,
        hasTenantState: !!tenantState
      });
      
      if (!tenantCode) {
        message.error('租户代码不能为空，请检查配置');
        return;
      }
      
      // 如果租户状态存在，使用状态中的数据；否则从表单获取
      let adminUser, services, dataInit;
      
      if (tenantState) {
        console.log('📋 使用租户状态中的数据');
        adminUser = tenantState.adminUser;
        services = tenantState.services;
        dataInit = tenantState.dataInit;
      } else {
        console.log('📝 从表单获取数据');
        adminUser = {
          username: values.adminUsername || '',
          password: values.adminPassword || '',
          email: values.adminEmail || ''
        };
        services = {
          sapB1: values.enableSapB1 ? {
            serverUrl: values.sapServerUrl || '',
            username: values.sapUsername || '',
            password: values.sapPassword || '',
            company: values.sapCompany || ''
          } : undefined,
          documentParsing: values.enableDocumentParsing ? {
            serverUrl: values.docServerUrl || '',
            username: values.docUsername || '',
            password: values.docPassword || ''
          } : undefined,
          chatbi: values.enableChatbi ? {
            serverUrl: values.chatbiServerUrl || '',
            username: values.chatbiUsername || '',
            password: values.chatbiPassword || ''
          } : undefined
        };
        dataInit = {
          includeModeling: values.includeModeling !== undefined ? values.includeModeling : true,
          includeInstructions: values.includeInstructions !== undefined ? values.includeInstructions : true,
          includeTemplates: values.includeTemplates !== undefined ? values.includeTemplates : true,
          includePermissions: values.includePermissions !== undefined ? values.includePermissions : true,
          syncToRemote: values.syncToRemote !== undefined ? values.syncToRemote : false,
          syncModeling: values.syncModeling !== undefined ? values.syncModeling : false,
          syncInstructions: values.syncInstructions !== undefined ? values.syncInstructions : false,
          syncTemplates: values.syncTemplates !== undefined ? values.syncTemplates : false,
          syncPermissions: values.syncPermissions !== undefined ? values.syncPermissions : false
        };
      }
      
      console.log('📝 最终使用的数据:', {
        tenantCode,
        tenantName,
        adminUser,
        services,
        dataInit
      });
      
      // 构建完整的步骤数据，包含所有累积的信息
      const stepData = {
        tenantCode: tenantCode,
        tenantName: tenantName,
        adminUser: adminUser,
        services: services,
        dataInit: dataInit
      };
      
      
      const initRequest: TenantInitRequest = {
        tenantCode: stepData.tenantCode,
        tenantName: stepData.tenantName,
        adminUser: stepData.adminUser,
        services: stepData.services,
        dataInit: stepData.dataInit
      };

      console.log('📤 发送初始化请求:', initRequest);
      console.log('📤 请求体大小:', JSON.stringify(initRequest).length);
      console.log('📤 请求体详情:', {
        tenantCode: initRequest.tenantCode,
        tenantName: initRequest.tenantName,
        adminUser: initRequest.adminUser,
        services: initRequest.services,
        dataInit: initRequest.dataInit
      });
      
      setIsInitializing(true);
      
      // 启动后重新加载租户状态
      if (tenantCode) {
        setTimeout(() => {
          loadTenantState(tenantCode);
        }, 1000);
      }
      
      const result = await tenantInitService.initializeTenant(initRequest);
      
      if (result.success) {
        message.success('初始化已开始，请等待完成...');
        startProgressPolling(initRequest.tenantCode);
        
        // 在编辑模式下，调用onComplete回调
        if (mode === 'edit' && onComplete) {
          onComplete();
        }
      } else {
        message.error(result.message || '初始化失败');
        setIsInitializing(false);
        setCurrentStep(3);
      }
    } catch (error) {
      console.error('开始初始化失败:', error);
      message.error('初始化失败');
      setIsInitializing(false);
      setCurrentStep(3);
    }
  };

  // 获取步骤状态
  const getStepStatus = (stepIndex: number) => {
    if (!initProgress) return 'wait';
    
    if (stepIndex < 4) {
      return currentStep > stepIndex ? 'finish' : 'wait';
    } else {
      // 初始化步骤
      if (initProgress.status === 'completed') return 'finish';
      if (initProgress.status === 'failed') return 'error';
      if (initProgress.status === 'initializing') return 'process';
      return 'wait';
    }
  };

  // 获取步骤图标
  const getStepIcon = (stepIndex: number) => {
    if (stepIndex < 4) {
      return stepConfigs[stepIndex].icon;
    } else {
      if (!initProgress) return <SyncOutlined />;
      
      switch (initProgress.status) {
        case 'completed':
          return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        case 'failed':
          return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
        case 'initializing':
          return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
        default:
          return <SyncOutlined />;
      }
    }
  };

  // 渲染步骤内容
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Card title="租户基本信息" size="small">
            <Form.Item
              name="tenantCode"
              label="租户代码"
              rules={[
                { required: true, message: '请输入租户代码' },
                { pattern: /^[a-zA-Z0-9_-]+$/, message: '租户代码只能包含字母、数字、下划线和连字符' }
              ]}
            >
              <Input placeholder="例如: company-001" />
            </Form.Item>
            
            <Form.Item
              name="tenantName"
              label="租户名称"
              rules={[{ required: true, message: '请输入租户名称' }]}
            >
              <Input placeholder="例如: 公司名称" />
            </Form.Item>
          </Card>
        );

      case 1:
        return (
          <Card title="管理员用户信息" size="small">
            {/* 隐藏的租户信息字段，确保表单状态保持 */}
            <Form.Item name="tenantCode" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="tenantName" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            
            <Form.Item
              name="adminUsername"
              label="用户名"
              rules={[
                { required: true, message: '请输入管理员用户名' },
                { min: 3, message: '用户名至少3个字符' }
              ]}
            >
              <Input placeholder="管理员用户名" />
            </Form.Item>
            
            <Form.Item
              name="adminPassword"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6个字符' }
              ]}
            >
              <Input.Password placeholder="管理员密码" />
            </Form.Item>
            
            <Form.Item
              name="adminEmail"
              label="邮箱"
              rules={[
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
            >
              <Input placeholder="管理员邮箱（可选）" />
            </Form.Item>
          </Card>
        );

      case 2:
        return (
          <Card title="外部服务配置" size="small">
            {/* 隐藏的字段，确保表单状态保持 */}
            <Form.Item name="tenantCode" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="tenantName" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="adminUsername" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="adminPassword" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="adminEmail" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Form.Item name="enableSapB1" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>启用 SAP B1 服务</Text>
              </div>
              
              <Form.Item shouldUpdate={(prev, curr) => prev.enableSapB1 !== curr.enableSapB1}>
                {({ getFieldValue }) => (
                  getFieldValue('enableSapB1') && (
                    <Card size="small" title="SAP B1 配置">
                      <Form.Item
                        name="sapServerUrl"
                        label="服务器地址"
                        rules={[{ required: true, message: '请输入SAP B1服务器地址' }]}
                      >
                        <Input placeholder="https://server.company.com" />
                      </Form.Item>
                      
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="sapUsername"
                            label="用户名"
                            rules={[{ required: true, message: '请输入SAP B1用户名' }]}
                          >
                            <Input placeholder="SAP B1用户名" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="sapPassword"
                            label="密码"
                            rules={[{ required: true, message: '请输入SAP B1密码' }]}
                          >
                            <Input.Password placeholder="SAP B1密码" />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      <Form.Item
                        name="sapCompany"
                        label="公司代码"
                        rules={[{ required: true, message: '请输入公司代码' }]}
                      >
                        <Input placeholder="例如: SBO_DEMO_AI" />
                      </Form.Item>
                    </Card>
                  )
                )}
              </Form.Item>

              <Divider />

              <div>
                <Form.Item name="enableDocumentParsing" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>启用文档解析服务</Text>
              </div>
              
              <Form.Item shouldUpdate={(prev, curr) => prev.enableDocumentParsing !== curr.enableDocumentParsing}>
                {({ getFieldValue }) => (
                  getFieldValue('enableDocumentParsing') && (
                    <Card size="small" title="文档解析服务配置">
                      <Form.Item
                        name="docServerUrl"
                        label="服务器地址"
                        rules={[{ required: true, message: '请输入文档解析服务器地址' }]}
                      >
                        <Input placeholder="https://analysis.company.com" />
                      </Form.Item>
                      
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="docUsername"
                            label="用户名"
                            rules={[{ required: true, message: '请输入文档解析用户名' }]}
                          >
                            <Input placeholder="文档解析用户名" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="docPassword"
                            label="密码"
                            rules={[{ required: true, message: '请输入文档解析密码' }]}
                          >
                            <Input.Password placeholder="文档解析密码" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  )
                )}
              </Form.Item>

              <Divider />

              <div>
                <Form.Item name="enableChatbi" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>启用 ChatBI 服务</Text>
              </div>
              
              <Form.Item shouldUpdate={(prev, curr) => prev.enableChatbi !== curr.enableChatbi}>
                {({ getFieldValue }) => (
                  getFieldValue('enableChatbi') && (
                    <Card size="small" title="ChatBI 服务配置">
                      <Form.Item
                        name="chatbiServerUrl"
                        label="服务器地址"
                        rules={[{ required: true, message: '请输入ChatBI服务器地址' }]}
                      >
                        <Input placeholder="https://chatbi.company.com" />
                      </Form.Item>
                      
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="chatbiUsername"
                            label="用户名"
                            rules={[{ required: true, message: '请输入ChatBI用户名' }]}
                          >
                            <Input placeholder="ChatBI用户名" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="chatbiPassword"
                            label="密码"
                            rules={[{ required: true, message: '请输入ChatBI密码' }]}
                          >
                            <Input.Password placeholder="ChatBI密码" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  )
                )}
              </Form.Item>
            </Space>
          </Card>
        );

      case 3:
        return (
          <Card title="数据初始化选项" size="small">
            {/* 隐藏的字段，确保表单状态保持 */}
            <Form.Item name="tenantCode" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="tenantName" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="adminUsername" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="adminPassword" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="adminEmail" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="enableSapB1" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="enableDocumentParsing" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="enableChatbi" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            
            {/* SAP B1 服务配置字段 */}
            <Form.Item name="sapServerUrl" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="sapUsername" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="sapPassword" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="sapCompany" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            
            {/* Document Parsing 服务配置字段 */}
            <Form.Item name="docServerUrl" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="docUsername" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="docPassword" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            
            {/* ChatBI 服务配置字段 */}
            <Form.Item name="chatbiServerUrl" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="chatbiUsername" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item name="chatbiPassword" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Form.Item name="includeModeling" valuePropName="checked" noStyle>
                  <Switch 
                    onChange={(checked) => {
                      console.log('🔄 includeModeling 开关变化:', checked);
                      handleDataInitChange('includeModeling', checked);
                    }} 
                  />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>初始化数据建模（Modeling & Models）</Text>
              </div>
              
              <div>
                <Form.Item name="includeInstructions" valuePropName="checked" noStyle>
                  <Switch 
                    onChange={(checked) => {
                      console.log('🔄 includeInstructions 开关变化:', checked);
                      handleDataInitChange('includeInstructions', checked);
                    }} 
                  />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>初始化指令配置（Instructions & Questions）</Text>
              </div>
              
              <div>
                <Form.Item name="includeTemplates" valuePropName="checked" noStyle>
                  <Switch 
                    onChange={(checked) => {
                      console.log('🔄 includeTemplates 开关变化:', checked);
                      handleDataInitChange('includeTemplates', checked);
                    }} 
                  />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>初始化模板管理（Templates & Properties）</Text>
              </div>
              
              <div>
                <Form.Item name="includePermissions" valuePropName="checked" noStyle>
                  <Switch 
                    onChange={(checked) => {
                      console.log('🔄 includePermissions 开关变化:', checked);
                      handleDataInitChange('includePermissions', checked);
                    }} 
                  />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>初始化权限系统（Roles & Permissions）</Text>
              </div>
              
              <Divider />
              
              <Text strong style={{ fontSize: '16px', marginBottom: '16px', display: 'block' }}>同步到远程服务</Text>
              
              <div>
                <Form.Item name="syncModeling" valuePropName="checked" noStyle>
                  <Switch 
                    onChange={(checked) => {
                      console.log('🔄 syncModeling 开关变化:', checked);
                      handleDataInitChange('syncModeling', checked);
                    }} 
                  />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>同步数据建模（Modeling & Models）</Text>
              </div>
              
              <div>
                <Form.Item name="syncInstructions" valuePropName="checked" noStyle>
                  <Switch 
                    onChange={(checked) => {
                      console.log('🔄 syncInstructions 开关变化:', checked);
                      handleDataInitChange('syncInstructions', checked);
                    }} 
                  />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>同步指令（Instructions & Questions）</Text>
              </div>
              
              <div>
                <Form.Item name="syncTemplates" valuePropName="checked" noStyle>
                  <Switch 
                    onChange={(checked) => {
                      console.log('🔄 syncTemplates 开关变化:', checked);
                      handleDataInitChange('syncTemplates', checked);
                    }} 
                  />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>同步模板（Templates & Properties）</Text>
              </div>
              
              <div>
                <Form.Item name="syncPermissions" valuePropName="checked" noStyle>
                  <Switch 
                    onChange={(checked) => {
                      console.log('🔄 syncPermissions 开关变化:', checked);
                      handleDataInitChange('syncPermissions', checked);
                    }} 
                  />
                </Form.Item>
                <Text style={{ marginLeft: 8 }}>同步权限（Roles & Permissions）</Text>
              </div>
              
              <Form.Item 
                name="syncToRemote" 
                valuePropName="checked"
                style={{ display: 'none' }}
              >
                <Switch 
                  onChange={(checked) => {
                    console.log('🔄 syncToRemote 开关变化:', checked);
                    handleDataInitChange('syncToRemote', checked);
                  }} 
                />
                <Text style={{ marginLeft: 8 }}>同步数据到远程服务</Text>
              </Form.Item>
            </Space>
          </Card>
        );

      case 4:
        // 构建表格数据，过滤掉配置任务（1-4步骤，即包含-config后缀的步骤）
        const tableData = tenantState?.steps
          ?.filter((step: any) => !step.id.includes('-config'))
          ?.map((step: any, index: number) => ({
            key: step.id,
            index: index + 1,
            name: step.name,
            description: step.description,
            status: step.status,
            progress: step.progress,
            message: step.message,
            error: step.error,
            startTime: step.startTime,
            endTime: step.endTime,
            duration: step.duration,
            stepId: step.id
          })) || [];

        // 表格列定义
        const columns = [
          {
            title: '#',
            dataIndex: 'index',
            width: 60,
            align: 'center' as const
          },
          {
            title: '步骤名称',
            dataIndex: 'name',
            width: 200,
            render: (text: string, record: any) => (
              <Space>
                {getStepStatusIcon(record.status)}
                <Text strong={record.status === 'running'}>{text}</Text>
              </Space>
            )
          },
          {
            title: '描述',
            dataIndex: 'description',
            ellipsis: true
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            align: 'center' as const,
            render: (status: string) => (
              <Tag color={getStepStatusColor(status)}>
                {status === 'completed' ? '完成' :
                 status === 'failed' ? '失败' :
                 status === 'running' ? '执行中' :
                 status === 'pending' ? '等待' : status}
              </Tag>
            )
          },
          {
            title: '进度',
            dataIndex: 'progress',
            width: 80,
            align: 'center' as const,
            render: (progress: number, record: any) => (
              record.status === 'running' ? (
                <Progress percent={progress} size="small" />
              ) : (
                <Text>{progress}%</Text>
              )
            )
          },
          {
            title: '开始时间',
            dataIndex: 'startTime',
            width: 150,
            render: (time: string) => formatTime(time)
          },
          {
            title: '结束时间',
            dataIndex: 'endTime',
            width: 150,
            render: (time: string) => formatTime(time)
          },
          {
            title: '耗时',
            dataIndex: 'duration',
            width: 80,
            align: 'center' as const,
            render: (duration: number) => formatDuration(duration)
          },
          {
            title: '操作',
            key: 'action',
            width: 150,
            align: 'center' as const,
            render: (_: any, record: any) => (
              <Space size="small">
                <Tooltip title="查看执行日志">
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      console.log('🔍 点击查看日志按钮:', record.stepId, record.name);
                      showStepLog(record.stepId, record.name);
                    }}
                  >
                    日志
                  </Button>
                </Tooltip>
                {record.status === 'pending' && (
                  <Popconfirm
                    title="确定要启动此步骤吗？"
                    onConfirm={() => retryStep(record.stepId, record.name)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="启动执行">
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlayCircleOutlined />}
                      >
                        启动
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                )}
                {record.status === 'running' && (
                  <Popconfirm
                    title="确定要停止此任务吗？"
                    onConfirm={() => stopTask(record.stepId, record.name)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="停止任务">
                      <Button
                        type="link"
                        size="small"
                        icon={<StopOutlined />}
                        danger
                      >
                        停止
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                )}
                {(record.status === 'failed' || record.status === 'completed' || record.status === 'cancelled') && (
                  <Popconfirm
                    title="确定要重新执行此步骤吗？"
                    onConfirm={() => retryStep(record.stepId, record.name)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="重新执行">
                      <Button
                        type="link"
                        size="small"
                        icon={<ReloadOutlined />}
                        danger={record.status === 'failed' || record.status === 'cancelled'}
                      >
                        重试
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            )
          }
        ];

        return (
          <Card title="开始初始化" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* 状态和按钮区域 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <Text strong>租户状态: </Text>
                  <Tag color={
                    tenantState?.status === 'config' ? 'blue' :
                    tenantState?.status === 'completed' ? 'green' :
                    tenantState?.status === 'failed' ? 'red' :
                    tenantState?.status === 'initializing' ? 'orange' : 'default'
                  }>
                    {tenantState?.status || '未知'}
                  </Tag>
                  {tenantState && (
                    <Text style={{ marginLeft: 16 }}>
                      完成步骤: {tenantState.completedSteps}/{tenantState.totalSteps}
                    </Text>
                  )}
                </div>
                <div>
                  {tenantState?.status === 'config' ? (
                    <Button 
                      type="primary" 
                      onClick={handleStartInit}
                      loading={isInitializing}
                      icon={<PlayCircleOutlined />}
                    >
                      启动初始化
                    </Button>
                  ) : (
                    <Button 
                      type="primary" 
                      onClick={handleStartInit}
                      loading={isInitializing}
                      icon={<ReloadOutlined />}
                    >
                      重新初始化
                    </Button>
                  )}
                </div>
              </div>

              {/* 整体进度 */}
              {tenantState && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>整体进度: </Text>
                  <Progress
                    percent={Math.round((tenantState.completedSteps / tenantState.totalSteps) * 100)}
                    status={tenantState.status === 'failed' ? 'exception' : 
                           tenantState.status === 'completed' ? 'success' : 'active'}
                  />
                </div>
              )}

              {/* 步骤执行表格 */}
              <div>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>步骤执行详情:</Text>
                <Table
                  columns={columns}
                  dataSource={tableData}
                  pagination={false}
                  size="small"
                  scroll={{ x: 1000 }}
                  rowClassName={(record) => {
                    switch (record.status) {
                      case 'completed': return 'table-row-success';
                      case 'failed': return 'table-row-error';
                      case 'running': return 'table-row-processing';
                      default: return '';
                    }
                  }}
                />
              </div>

              {/* 实时进度显示（保留用于调试） */}
              {initProgress && (
                <div style={{ marginTop: 16 }}>
                  <Text strong>实时进度: </Text>
                  <Progress
                    percent={Math.round((initProgress.completedSteps / initProgress.totalSteps) * 100)}
                    status={initProgress.status === 'failed' ? 'exception' : 'active'}
                  />
                  
                  <div style={{ marginTop: 8 }}>
                    <Text strong>当前步骤: </Text>
                    <Tag color={initProgress.status === 'failed' ? 'red' : 'blue'}>
                      {initProgress.steps.find(s => s.id === initProgress.currentStep)?.name || '未知'}
                    </Tag>
                  </div>
                </div>
              )}
            </Space>
          </Card>
        );

      default:
        return null;
    }
  };

  // 处理步骤切换
  const handleStepChange = (step: number) => {
    if (isInitializing) return;
    setCurrentStep(step);
  };

  // 处理dataInit字段变化（仅本地状态，不发送到后台）
  const handleDataInitChange = (fieldName: string, value: boolean) => {
    console.log(`🔄 dataInit字段变化: ${fieldName} = ${value}`);
    
    // 获取当前表单值作为基础
    const formValues = form.getFieldsValue();
    console.log('📋 当前表单值:', formValues);
    
    // 构建当前的dataInit状态，使用传入的value更新对应字段，其他字段保持当前值
    const currentDataInit = {
      includeModeling: fieldName === 'includeModeling' ? value : (formValues.includeModeling !== undefined ? formValues.includeModeling : true),
      includeInstructions: fieldName === 'includeInstructions' ? value : (formValues.includeInstructions !== undefined ? formValues.includeInstructions : true),
      includeTemplates: fieldName === 'includeTemplates' ? value : (formValues.includeTemplates !== undefined ? formValues.includeTemplates : true),
      includePermissions: fieldName === 'includePermissions' ? value : (formValues.includePermissions !== undefined ? formValues.includePermissions : true),
      syncToRemote: fieldName === 'syncToRemote' ? value : (formValues.syncToRemote !== undefined ? formValues.syncToRemote : false),
      syncModeling: fieldName === 'syncModeling' ? value : (formValues.syncModeling !== undefined ? formValues.syncModeling : false),
      syncInstructions: fieldName === 'syncInstructions' ? value : (formValues.syncInstructions !== undefined ? formValues.syncInstructions : false),
      syncTemplates: fieldName === 'syncTemplates' ? value : (formValues.syncTemplates !== undefined ? formValues.syncTemplates : false),
      syncPermissions: fieldName === 'syncPermissions' ? value : (formValues.syncPermissions !== undefined ? formValues.syncPermissions : false)
    };
    
    console.log('💾 当前dataInit状态:', currentDataInit);
    console.log('ℹ️ dataInit变化仅保存在本地，将在点击"开始初始化"时一起发送到后台');
    
    // 立即更新表单值，确保表单状态与我们的状态保持一致
    form.setFieldsValue(currentDataInit);
  };

  // 获取租户状态
  const loadTenantState = useCallback(async (tenantCode: string) => {
    try {
      console.log('🔄 开始获取租户状态，租户代码:', tenantCode);
      const result = await tenantInitService.getTenantState(tenantCode);
      
      console.log('📡 API 响应结果:', result);
      console.log('📡 API 响应 success:', result.success);
      console.log('📡 API 响应 data:', result.data);
      console.log('📡 API 响应 message:', result.message);
      
      if (result.success && result.data) {
        console.log('📋 租户状态数据:', result.data);
        console.log('🔄 调用 setTenantState，传入数据:', result.data);
        setTenantState(result.data);
        console.log('✅ setTenantState 调用完成');
        
        // 设置表单的基本信息和服务配置（编辑模式和创建模式都需要）
        const formValues = {
          tenantCode: result.data.tenantCode,
          tenantName: result.data.tenantName,
          adminUsername: result.data.adminUser?.username,
          adminPassword: result.data.adminUser?.password,
          adminEmail: result.data.adminUser?.email,
          // 服务配置
          enableSapB1: !!result.data.services?.sapB1,
          sapServerUrl: result.data.services?.sapB1?.serverUrl || '',
          sapUsername: result.data.services?.sapB1?.username || '',
          sapPassword: result.data.services?.sapB1?.password || '',
          sapCompany: result.data.services?.sapB1?.company || '',
          enableDocumentParsing: !!result.data.services?.documentParsing,
          docServerUrl: result.data.services?.documentParsing?.serverUrl || '',
          docUsername: result.data.services?.documentParsing?.username || '',
          docPassword: result.data.services?.documentParsing?.password || '',
          enableChatbi: !!result.data.services?.chatbi,
          chatbiServerUrl: result.data.services?.chatbi?.serverUrl || '',
          chatbiUsername: result.data.services?.chatbi?.username || '',
          chatbiPassword: result.data.services?.chatbi?.password || '',
          // 数据初始化配置
          includeModeling: result.data.dataInit?.includeModeling !== undefined ? result.data.dataInit.includeModeling : true,
          includeInstructions: result.data.dataInit?.includeInstructions !== undefined ? result.data.dataInit.includeInstructions : true,
          includeTemplates: result.data.dataInit?.includeTemplates !== undefined ? result.data.dataInit.includeTemplates : true,
          includePermissions: result.data.dataInit?.includePermissions !== undefined ? result.data.dataInit.includePermissions : true,
          syncToRemote: result.data.dataInit?.syncToRemote !== undefined ? result.data.dataInit.syncToRemote : false,
          syncModeling: result.data.dataInit?.syncModeling !== undefined ? result.data.dataInit.syncModeling : false,
          syncInstructions: result.data.dataInit?.syncInstructions !== undefined ? result.data.dataInit.syncInstructions : false,
          syncTemplates: result.data.dataInit?.syncTemplates !== undefined ? result.data.dataInit.syncTemplates : false,
          syncPermissions: result.data.dataInit?.syncPermissions !== undefined ? result.data.dataInit.syncPermissions : false
        };
        
        form.setFieldsValue(formValues);
        console.log('📝 设置表单完整信息:', formValues);
        
        // 构建job执行详情
        const details = buildJobDetails(result.data);
        setJobDetails(details);
        
        // 验证租户状态是否正确设置
        console.log('✅ 租户状态设置完成，设置的tenantState:', result.data);
      } else {
        console.warn('⚠️ 获取租户状态失败:', result.message);
        setTenantState(null);
      }
    } catch (error) {
      console.error('❌ 获取租户状态失败:', error);
      setTenantState(null);
    }
  }, [form, tenantState]);

  // 重新执行单个步骤
  const retryStep = async (stepId: string, stepName: string) => {
    try {
      const tenantCode = propTenantCode || form.getFieldValue('tenantCode');
      if (!tenantCode) {
        message.error('租户代码不能为空');
        return;
      }

      console.log(`🔄 重新执行步骤: ${stepId} (${stepName})`);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/tenants/${tenantCode}/step/${stepId}/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        message.success(`步骤 ${stepName} 重新执行成功`);
        // 重新加载租户状态
        await loadTenantState(tenantCode);
      } else {
        message.error(result.message || `步骤 ${stepName} 重新执行失败`);
      }
    } catch (error) {
      console.error('重新执行步骤失败:', error);
      message.error('重新执行步骤失败');
    }
  };

  // 构建job执行详情
  const buildJobDetails = (state: any) => {
    if (!state || !state.steps) return '暂无执行详情';
    
    let details = `租户: ${state.tenantCode}\n`;
    details += `状态: ${state.status}\n`;
    details += `当前步骤: ${state.currentStep}\n`;
    details += `完成步骤: ${state.completedSteps}/${state.totalSteps}\n`;
    details += `开始时间: ${state.startTime}\n`;
    if (state.endTime) {
      details += `结束时间: ${state.endTime}\n`;
    }
    if (state.totalDuration) {
      details += `总耗时: ${Math.round(state.totalDuration / 1000)}s\n`;
    }
    details += `\n步骤详情:\n`;
    
    state.steps.forEach((step: any, index: number) => {
      details += `${index + 1}. ${step.name} (${step.id})\n`;
      details += `   状态: ${step.status}\n`;
      details += `   进度: ${step.progress}%\n`;
      if (step.message) {
        details += `   消息: ${step.message}\n`;
      }
      if (step.error) {
        details += `   错误: ${step.error}\n`;
      }
      if (step.startTime) {
        details += `   开始时间: ${step.startTime}\n`;
      }
      if (step.endTime) {
        details += `   结束时间: ${step.endTime}\n`;
      }
      if (step.duration) {
        details += `   耗时: ${Math.round(step.duration / 1000)}s\n`;
      }
      details += `\n`;
    });
    
    return details;
  };

  // 获取步骤状态颜色
  const getStepStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running': return 'processing';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  // 获取步骤状态图标
  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed': return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'running': return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      case 'pending': return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
      default: return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  // 格式化时间显示
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '-';
    try {
      return new Date(timeStr).toLocaleString('zh-CN');
    } catch {
      return timeStr;
    }
  };

  // 格式化耗时显示
  const formatDuration = (duration: number) => {
    if (!duration) return '-';
    return `${Math.round(duration / 1000)}s`;
  };

  // 查看步骤执行日志
  const showStepLog = async (stepId: string, stepName: string) => {
    try {
      const tenantCode = propTenantCode || form.getFieldValue('tenantCode');
      if (!tenantCode) {
        message.error('租户代码不能为空');
        return;
      }

      console.log(`📋 查看步骤日志: ${stepId} (${stepName}), 租户: ${tenantCode}`);
      
      // 设置当前查看的步骤信息
      console.log('🔧 设置日志模态框状态...');
      setCurrentStepLog({ stepId, stepName });
      setCurrentStepId(stepId);
      setLogModalVisible(true);
      setStepLogs('正在加载日志...');
      setRealTimeLogs([]);
      console.log('✅ 日志模态框状态已设置，visible:', true);

      // 获取步骤日志
      const token = localStorage.getItem('token');
      console.log(`🔍 请求日志API: ${API_BASE_URL}/tenants/${tenantCode}/step/${stepId}/logs`);
      
      const response = await fetch(`${API_BASE_URL}/tenants/${tenantCode}/step/${stepId}/logs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log(`📡 日志API响应状态: ${response.status}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`📋 日志API响应数据:`, result);
      
      if (result.success && result.data) {
        setStepLogs(result.data);
        console.log(`✅ 日志加载成功，长度: ${result.data.length}`);
      } else {
        const errorMsg = `获取日志失败: ${result.message || '未知错误'}`;
        setStepLogs(errorMsg);
        console.error(`❌ ${errorMsg}`);
      }

      // 建立SSE连接用于实时日志
      console.log(`🔗 建立SSE连接...`);
      establishSSEConnection(tenantCode, stepId);
    } catch (error) {
      console.error('❌ 获取步骤日志失败:', error);
      const errorMsg = `获取日志失败: ${error instanceof Error ? error.message : String(error)}`;
      setStepLogs(errorMsg);
      message.error(errorMsg);
    }
  };

  // 建立SSE连接
  const establishSSEConnection = (tenantCode: string, stepId: string) => {
    try {
      // 关闭现有连接
      if (eventSource) {
        console.log('🔌 关闭现有SSE连接');
        eventSource.close();
      }

      const token = localStorage.getItem('token');
      const sseUrl = `${API_BASE_URL}/tenants/${tenantCode}/step/${stepId}/logs/stream`;
      console.log(`🔗 建立SSE连接: ${sseUrl}`);
      
      const es = new EventSource(sseUrl);

      es.onopen = () => {
        console.log('✅ SSE连接已建立');
        setIsConnected(true);
        setRealTimeLogs(prev => [...prev, '[连接已建立] 开始接收实时日志...']);
      };

      es.onmessage = (event) => {
        try {
          console.log('📨 收到SSE消息:', event.data);
          const data = JSON.parse(event.data);
          
          if (data.type === 'connected') {
            setRealTimeLogs(prev => [...prev, `[系统] ${data.message}`]);
          } else if (data.type === 'log') {
            const timestamp = new Date(data.timestamp).toLocaleTimeString();
            const logType = data.logType === 'error' ? '❌' : data.logType === 'success' ? '✅' : 'ℹ️';
            setRealTimeLogs(prev => [...prev, `[${timestamp}] ${logType} ${data.message}`]);
          }
        } catch (error) {
          console.error('❌ 解析SSE消息失败:', error);
          setRealTimeLogs(prev => [...prev, `[错误] 解析消息失败: ${event.data}`]);
        }
      };

      es.onerror = (error) => {
        console.error('❌ SSE连接错误:', error);
        setIsConnected(false);
        setRealTimeLogs(prev => [...prev, '[错误] SSE连接中断']);
        es.close();
      };

      setEventSource(es);
    } catch (error) {
      console.error('❌ 建立SSE连接失败:', error);
      setIsConnected(false);
      setRealTimeLogs(prev => [...prev, `[错误] 建立连接失败: ${error instanceof Error ? error.message : String(error)}`]);
    }
  };

  // 停止任务
  const stopTask = async (stepId: string, stepName: string) => {
    try {
      const tenantCode = propTenantCode || form.getFieldValue('tenantCode');
      if (!tenantCode) {
        message.error('租户代码不能为空');
        return;
      }

      console.log(`🛑 停止任务: ${stepId} (${stepName})`);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/tenants/${tenantCode}/step/${stepId}/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.success) {
        message.success('任务已停止');
        // 重新加载租户状态
        const tenantCode = propTenantCode || form.getFieldValue('tenantCode');
        if (tenantCode) {
          loadTenantState(tenantCode);
        }
      } else {
        message.error(`停止任务失败: ${result.message || '未知错误'}`);
      }
    } catch (error) {
      console.error('停止任务失败:', error);
      message.error(`停止任务失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  // 关闭日志模态框
  const closeLogModal = () => {
    // 关闭SSE连接
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
    
    setLogModalVisible(false);
    setCurrentStepLog(null);
    setStepLogs('');
    setRealTimeLogs([]);
    setIsConnected(false);
    setCurrentStepId('');
  };

  // 从状态文件中加载dataInit的值
  const loadDataInitFromState = useCallback(async (tenantCode: string) => {
    try {
      console.log('🔄 开始从状态文件恢复dataInit值，租户代码:', tenantCode);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/tenants/${tenantCode}/state`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('📡 状态文件响应状态:', response.status);
      
      if (response.ok) {
        const state = await response.json();
        console.log('📋 完整状态数据:', state);
        
        // 处理API返回的数据结构 {success: true, data: {...}}
        const actualState = state.data || state;
        console.log('📋 实际状态数据:', actualState);
        
        if (actualState.dataInit) {
          console.log('🔄 从状态文件恢复dataInit值:', actualState.dataInit);
          // 设置表单的dataInit字段值
          const dataInitValues = {
            includeModeling: actualState.dataInit.includeModeling,
            includeInstructions: actualState.dataInit.includeInstructions,
            includeTemplates: actualState.dataInit.includeTemplates,
            includePermissions: actualState.dataInit.includePermissions,
            syncToRemote: actualState.dataInit.syncToRemote,
            syncModeling: actualState.dataInit.syncModeling || false,
            syncInstructions: actualState.dataInit.syncInstructions || false,
            syncTemplates: actualState.dataInit.syncTemplates || false,
            syncPermissions: actualState.dataInit.syncPermissions || false
          };
          
          console.log('💾 设置表单dataInit值:', dataInitValues);
          form.setFieldsValue(dataInitValues);
          
          // 验证设置是否成功
          setTimeout(() => {
            const currentValues = form.getFieldsValue();
            console.log('✅ 设置后的表单值:', currentValues);
          }, 100);
        } else {
          console.warn('⚠️ 状态文件中没有dataInit数据，使用默认值');
          // 设置默认的dataInit值
          const defaultDataInitValues = {
            includeModeling: true,
            includeInstructions: true,
            includeTemplates: true,
            includePermissions: true,
            syncToRemote: false,
            syncModeling: false,
            syncInstructions: false,
            syncTemplates: false,
            syncPermissions: false
          };
          
          console.log('💾 设置默认dataInit值:', defaultDataInitValues);
          form.setFieldsValue(defaultDataInitValues);
          
          // 验证设置是否成功
          setTimeout(() => {
            const currentValues = form.getFieldsValue();
            console.log('✅ 设置后的表单值:', currentValues);
          }, 100);
        }
      } else {
        console.error('❌ 获取状态文件失败:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ 加载dataInit状态失败:', error);
    }
  }, [form]);

  // 保存步骤状态到后台
  const saveStepState = async (stepId: string, stepData: any, stepStatus: string = 'completed') => {
    try {
      // 优先使用传入的stepData，如果没有则从表单获取
      const formValues = form.getFieldsValue();
      const tenantCode = stepData?.tenantCode || formValues.tenantCode;
      
      console.log(`🔍 saveStepState 调试信息:`, {
        stepId,
        stepData,
        formValues,
        tenantCode,
        hasStepData: stepData && Object.keys(stepData).length > 0
      });
      
      if (!tenantCode) {
        console.warn('租户代码为空，跳过保存步骤状态');
        return;
      }

      // 构建完整的数据对象，优先使用stepData，如果没有则使用formValues
      const fullStepData = {
        tenantCode: stepData?.tenantCode || formValues.tenantCode,
        tenantName: stepData?.tenantName || formValues.tenantName,
        adminUser: {
          username: stepData?.adminUser?.username || formValues.adminUsername || '',
          password: stepData?.adminUser?.password || formValues.adminPassword || '',
          email: stepData?.adminUser?.email || formValues.adminEmail || ''
        },
        services: {
          sapB1: (stepData?.services?.sapB1 || (formValues.enableSapB1 ? {
            serverUrl: formValues.sapServerUrl || '',
            username: formValues.sapUsername || '',
            password: formValues.sapPassword || '',
            company: formValues.sapCompany || ''
          } : undefined)),
          documentParsing: (stepData?.services?.documentParsing || (formValues.enableDocumentParsing ? {
            serverUrl: formValues.docServerUrl || '',
            username: formValues.docUsername || '',
            password: formValues.docPassword || ''
          } : undefined)),
          chatbi: (stepData?.services?.chatbi || (formValues.enableChatbi ? {
            serverUrl: formValues.chatbiServerUrl || '',
            username: formValues.chatbiUsername || '',
            password: formValues.chatbiPassword || ''
          } : undefined))
        },
        dataInit: {
          includeModeling: stepData?.dataInit?.includeModeling !== undefined ? stepData.dataInit.includeModeling : (formValues.includeModeling !== undefined ? formValues.includeModeling : true),
          includeInstructions: stepData?.dataInit?.includeInstructions !== undefined ? stepData.dataInit.includeInstructions : (formValues.includeInstructions !== undefined ? formValues.includeInstructions : true),
          includeTemplates: stepData?.dataInit?.includeTemplates !== undefined ? stepData.dataInit.includeTemplates : (formValues.includeTemplates !== undefined ? formValues.includeTemplates : true),
          includePermissions: stepData?.dataInit?.includePermissions !== undefined ? stepData.dataInit.includePermissions : (formValues.includePermissions !== undefined ? formValues.includePermissions : true),
          syncToRemote: stepData?.dataInit?.syncToRemote !== undefined ? stepData.dataInit.syncToRemote : (formValues.syncToRemote !== undefined ? formValues.syncToRemote : false),
          syncModeling: stepData?.dataInit?.syncModeling !== undefined ? stepData.dataInit.syncModeling : (formValues.syncModeling !== undefined ? formValues.syncModeling : false),
          syncInstructions: stepData?.dataInit?.syncInstructions !== undefined ? stepData.dataInit.syncInstructions : (formValues.syncInstructions !== undefined ? formValues.syncInstructions : false),
          syncTemplates: stepData?.dataInit?.syncTemplates !== undefined ? stepData.dataInit.syncTemplates : (formValues.syncTemplates !== undefined ? formValues.syncTemplates : false),
          syncPermissions: stepData?.dataInit?.syncPermissions !== undefined ? stepData.dataInit.syncPermissions : (formValues.syncPermissions !== undefined ? formValues.syncPermissions : false)
        }
      };

      console.log(`💾 保存步骤 ${stepId} 数据:`, fullStepData);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/tenants/${tenantCode}/step`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          stepId,
          stepData: fullStepData,
          stepStatus
        })
      });

      const result = await response.json();
      if (result.success) {
        console.log(`✅ 步骤 ${stepId} 状态已保存`);
      } else {
        console.warn(`⚠️ 保存步骤 ${stepId} 状态失败:`, result.message);
      }
    } catch (error) {
      console.error(`❌ 保存步骤 ${stepId} 状态失败:`, error);
    }
  };

  // 处理下一步
  const handleNext = async () => {
    if (currentStep < 3) {
      try {
        await form.validateFields();
        
        // 等待一小段时间让表单状态完全更新
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 获取当前表单的所有值
        const formValues = form.getFieldsValue();
        console.log(`🔍 步骤 ${currentStep} 表单数据:`, formValues);
        
        // 构建完整的步骤数据
        const stepData = {
          tenantCode: formValues.tenantCode,
          tenantName: formValues.tenantName,
          adminUser: {
            username: formValues.adminUsername || '',
            password: formValues.adminPassword || '',
            email: formValues.adminEmail || ''
          },
          services: {
            sapB1: formValues.enableSapB1 ? {
              serverUrl: formValues.sapServerUrl || '',
              username: formValues.sapUsername || '',
              password: formValues.sapPassword || '',
              company: formValues.sapCompany || ''
            } : undefined,
            documentParsing: formValues.enableDocumentParsing ? {
              serverUrl: formValues.docServerUrl || '',
              username: formValues.docUsername || '',
              password: formValues.docPassword || ''
            } : undefined,
            chatbi: formValues.enableChatbi ? {
              serverUrl: formValues.chatbiServerUrl || '',
              username: formValues.chatbiUsername || '',
              password: formValues.chatbiPassword || ''
            } : undefined
          },
          dataInit: {
            includeModeling: formValues.includeModeling !== undefined ? formValues.includeModeling : true,
            includeInstructions: formValues.includeInstructions !== undefined ? formValues.includeInstructions : true,
            includeTemplates: formValues.includeTemplates !== undefined ? formValues.includeTemplates : true,
            includePermissions: formValues.includePermissions !== undefined ? formValues.includePermissions : true,
            syncToRemote: formValues.syncToRemote !== undefined ? formValues.syncToRemote : false,
            syncModeling: formValues.syncModeling !== undefined ? formValues.syncModeling : false,
            syncInstructions: formValues.syncInstructions !== undefined ? formValues.syncInstructions : false,
            syncTemplates: formValues.syncTemplates !== undefined ? formValues.syncTemplates : false,
            syncPermissions: formValues.syncPermissions !== undefined ? formValues.syncPermissions : false
          }
        };
        
        // 配置阶段的步骤映射（以-config结尾）
        const configStepMap = {
          0: 'tenant-creation-config',
          1: 'user-creation-config',
          2: 'service-config-config',
          3: 'modeling-init-config'
        };
        const stepId = configStepMap[currentStep as keyof typeof configStepMap];
        if (stepId) {
          await saveStepState(stepId, stepData, 'completed');
        }
        
        // 步骤状态已保存，useEffect会处理dataInit值的加载
        
        setCurrentStep(currentStep + 1);
      } catch (error) {
        message.error('请填写必填字段');
      }
    } else if (currentStep === 3) {
      // 步骤3保存配置并进入下一步
      try {
        await form.validateFields();
        
        // 等待一小段时间让表单状态完全更新
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // 获取当前表单的所有值
        const formValues = form.getFieldsValue();
        console.log(`🔍 步骤 ${currentStep} 表单数据:`, formValues);
        
        // 构建完整的步骤数据
        const stepData = {
          tenantCode: formValues.tenantCode,
          tenantName: formValues.tenantName,
          adminUser: {
            username: formValues.adminUsername || '',
            password: formValues.adminPassword || '',
            email: formValues.adminEmail || ''
          },
          services: {
            sapB1: formValues.enableSapB1 ? {
              serverUrl: formValues.sapServerUrl || '',
              username: formValues.sapUsername || '',
              password: formValues.sapPassword || '',
              company: formValues.sapCompany || ''
            } : undefined,
            documentParsing: formValues.enableDocumentParsing ? {
              serverUrl: formValues.docServerUrl || '',
              username: formValues.docUsername || '',
              password: formValues.docPassword || ''
            } : undefined,
            chatbi: formValues.enableChatbi ? {
              serverUrl: formValues.chatbiServerUrl || '',
              username: formValues.chatbiUsername || '',
              password: formValues.chatbiPassword || ''
            } : undefined
          },
          dataInit: {
            includeModeling: formValues.includeModeling !== undefined ? formValues.includeModeling : true,
            includeInstructions: formValues.includeInstructions !== undefined ? formValues.includeInstructions : true,
            includeTemplates: formValues.includeTemplates !== undefined ? formValues.includeTemplates : true,
            includePermissions: formValues.includePermissions !== undefined ? formValues.includePermissions : true,
            syncToRemote: formValues.syncToRemote !== undefined ? formValues.syncToRemote : false,
            syncModeling: formValues.syncModeling !== undefined ? formValues.syncModeling : false,
            syncInstructions: formValues.syncInstructions !== undefined ? formValues.syncInstructions : false,
            syncTemplates: formValues.syncTemplates !== undefined ? formValues.syncTemplates : false,
            syncPermissions: formValues.syncPermissions !== undefined ? formValues.syncPermissions : false
          }
        };
        
        // 保存配置到状态文件
        await saveStepState('modeling-init-config', stepData, 'completed');
        
        message.success('配置已保存');
        
        // 进入下一步
        setCurrentStep(currentStep + 1);
      } catch (error) {
        message.error('请填写必填字段');
      }
    }
  };

  // 处理上一步
  const handlePrev = () => {
    if (currentStep > 0 && !isInitializing) {
      setCurrentStep(currentStep - 1);
    }
  };

  // 处理字段变化，实时保存状态
  const handleFieldChange = async (changedFields: any, allFields: any) => {
    // 如果租户代码已填写，则保存当前步骤状态
    if (allFields.tenantCode && allFields.tenantCode.length > 0) {
      const configStepMap = {
        0: 'tenant-creation-config',
        1: 'user-creation-config',
        2: 'service-config-config',
        3: 'modeling-init-config'
      };
      const stepId = configStepMap[currentStep as keyof typeof configStepMap];
      if (stepId) {
        // 延迟保存，避免频繁调用
        setTimeout(() => {
          // 获取当前表单的所有值
          const formValues = form.getFieldsValue();
          saveStepState(stepId, formValues, 'running');
        }, 500);
      }
    }
  };

  // 处理关闭
  const handleClose = () => {
    if (isInitializing) {
      message.warning('初始化进行中，无法关闭');
      return;
    }
    
    if (progressPolling) {
      clearInterval(progressPolling);
      setProgressPolling(null);
    }
    
    form.resetFields();
    setCurrentStep(0);
    setInitProgress(null);
    setIsInitializing(false);
    if (onClose) {
      onClose();
    }
  };

  // 如果是在标签页中使用（没有onClose），直接渲染内容
  if (!onClose) {
    return (
      <div className="page-container">
        <div className="page-header">
          <Title level={2}>多租户初始化向导</Title>
          <p>创建新的租户并完成初始化配置</p>
        </div>
        <div className="page-content">
          {renderWizardContent()}
        </div>
      </div>
    );
  }

  // 如果是在Modal中使用，保持原有逻辑
  return (
    <>
      <Modal
        title="多租户初始化向导"
        open={visible}
        onCancel={handleClose}
        width={800}
        footer={null}
        closable={!isInitializing}
        maskClosable={!isInitializing}
      >
        {renderWizardContent()}
      </Modal>

      {/* 步骤执行日志模态框 */}
      <Modal
        title={`步骤执行日志 - ${currentStepLog?.stepName || ''}`}
        open={logModalVisible}
        onCancel={() => {
          console.log('🔚 关闭日志模态框');
          closeLogModal();
        }}
        width={1000}
        footer={[
          <Button key="stop" 
                  danger 
                  icon={<StopOutlined />}
                  onClick={() => currentStepId && stopTask(currentStepId, currentStepLog?.stepName || '')}
                  disabled={!currentStepId}
          >
            停止任务
          </Button>,
          <Button key="close" onClick={() => {
            console.log('🔚 点击关闭按钮');
            closeLogModal();
          }}>
            关闭
          </Button>
        ]}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Text strong>步骤ID: </Text>
              <Text code>{currentStepLog?.stepId || ''}</Text>
            </Col>
            <Col>
              <Tag color={isConnected ? 'green' : 'red'}>
                {isConnected ? '🔗 实时连接中' : '❌ 连接断开'}
              </Tag>
            </Col>
          </Row>
        </div>
        
        <Tabs defaultActiveKey="realtime" size="small">
          <Tabs.TabPane tab="实时日志" key="realtime">
            <div style={{ 
              height: '400px', 
              overflow: 'auto', 
              backgroundColor: '#f5f5f5',
              padding: '12px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '12px',
              lineHeight: '1.5'
            }}>
              {realTimeLogs.length === 0 ? (
                <Text type="secondary">等待实时日志...</Text>
              ) : (
                realTimeLogs.map((log, index) => (
                  <div key={index} style={{ marginBottom: '4px' }}>
                    {log}
                  </div>
                ))
              )}
            </div>
          </Tabs.TabPane>
          
          <Tabs.TabPane tab="历史日志" key="history">
            <Input.TextArea
              value={stepLogs}
              readOnly
              rows={20}
              style={{ 
                fontFamily: 'monospace',
                fontSize: '12px',
                backgroundColor: '#f5f5f5'
              }}
              placeholder="暂无历史日志内容"
            />
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </>
  );

  function renderWizardContent() {
    return (
      <>
        <div style={{ marginBottom: 24 }}>
          <Steps current={currentStep} onChange={handleStepChange}>
            {stepConfigs.map((config, index) => (
              <Step
                key={index}
                title={config.title}
                description={config.description}
                icon={getStepIcon(index)}
                status={getStepStatus(index)}
              />
            ))}
          </Steps>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFieldsChange={handleFieldChange}
          initialValues={{
            tenantCode: initialData?.tenantCode || '',
            tenantName: initialData?.tenantName || '',
            adminUsername: initialData?.adminUser?.username || '',
            adminPassword: initialData?.adminUser?.password || '',
            adminEmail: initialData?.adminUser?.email || '',
            enableSapB1: !!initialData?.services?.sapB1,
            enableDocumentParsing: !!initialData?.services?.documentParsing,
            enableChatbi: !!initialData?.services?.chatbi,
            // 服务配置详细字段
            sapServerUrl: initialData?.services?.sapB1?.serverUrl || '',
            sapUsername: initialData?.services?.sapB1?.username || '',
            sapPassword: initialData?.services?.sapB1?.password || '',
            sapCompany: initialData?.services?.sapB1?.company || '',
            docServerUrl: initialData?.services?.documentParsing?.serverUrl || '',
            docUsername: initialData?.services?.documentParsing?.username || '',
            docPassword: initialData?.services?.documentParsing?.password || '',
            chatbiServerUrl: initialData?.services?.chatbi?.serverUrl || '',
            chatbiUsername: initialData?.services?.chatbi?.username || '',
            chatbiPassword: initialData?.services?.chatbi?.password || '',
            // dataInit字段将在useEffect中设置
            includeModeling: initialData?.dataInit?.includeModeling ?? true,
            includeInstructions: initialData?.dataInit?.includeInstructions ?? true,
            includeTemplates: initialData?.dataInit?.includeTemplates ?? true,
            includePermissions: initialData?.dataInit?.includePermissions ?? true,
            syncToRemote: initialData?.dataInit?.syncToRemote ?? false,
            syncModeling: initialData?.dataInit?.syncModeling ?? false,
            syncInstructions: initialData?.dataInit?.syncInstructions ?? false,
            syncTemplates: initialData?.dataInit?.syncTemplates ?? false,
            syncPermissions: initialData?.dataInit?.syncPermissions ?? false
          }}
        >
          {renderStepContent()}
        </Form>

        {currentStep < 4 && (
          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={handlePrev} disabled={currentStep === 0 || isInitializing}>
                上一步
              </Button>
              <Button
                type="primary"
                onClick={handleNext}
                loading={isInitializing}
              >
                下一步
              </Button>
            </Space>
          </div>
        )}
      </>
    );
  }
};

export default TenantInitWizard;
